import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import OnlyFansAdStripe from "../components/OnlyFansAdStripe";
import "../styles/coaching.css";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import CountrySelect from "../components/CountrySelect";
import CountryFlag from "../components/CountryFlag";
import UserBadges from "../components/UserBadges";
// GlimtStrip fjernet fra Skapere-siden (v103). Komponent beholdt i
// /components/GlimtStrip.jsx for framtidig plassering (bunn-nav, egen side).

const GENDERS = ["", "kvinne", "mann", "ikke-binær", "par"];

function CreatorCard({ c, featured = false }) {
  const { t, i18n } = useTranslation();
  const initials = (c.username || "?").slice(0, 2).toUpperCase();
  const loc = [c.city, c.fylke].filter(Boolean).join(", ");
  const followerCount = c.follower_count || 0;
  return (
    <Link
      to={`/skaper/${c.username}`}
      data-testid={`creator-card-${c.username}`}
      className="group block overflow-hidden transition p-2 rounded"
      style={{
        background: "rgba(26, 20, 28, 0.95)",
        border: "2px solid rgba(255, 95, 168, 0.75)",
        boxShadow: "0 0 12px rgba(255, 95, 168, 0.25), inset 0 0 0 1px rgba(255, 95, 168, 0.15)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 32px rgba(255, 95, 168, 0.5), inset 0 0 0 1px rgba(255, 95, 168, 0.3)"; e.currentTarget.style.borderColor = "rgba(255, 95, 168, 1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 12px rgba(255, 95, 168, 0.25), inset 0 0 0 1px rgba(255, 95, 168, 0.15)"; e.currentTarget.style.borderColor = "rgba(255, 95, 168, 0.75)"; }}
    >
      {/* Badges FØR bildet, innenfor rammen */}
      <div className="flex gap-1.5 mb-2 px-0.5">
        <span className="font-meta text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 bg-[var(--magenta)]/15 border border-[var(--magenta)]/50 text-[var(--magenta)] rounded-sm">
          {t("creators_page.creator_tag")}
        </span>
        {featured && (
          <span className="font-meta text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 bg-[var(--gold)]/15 border border-[var(--gold)]/50 text-[var(--gold)] rounded-sm">
            {t("creators_page.featured_tag")}
          </span>
        )}
      </div>
      <div className="relative aspect-[3/4] bg-[var(--noir-elev)] overflow-hidden rounded-sm">
        {c.creator_avatar_url ? (
          <img
            src={c.creator_avatar_url}
            alt={c.username}
            className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-[var(--cream-faint)] font-fraunces text-5xl italic">
            {initials}
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-fraunces italic text-lg text-[var(--cream)] truncate">
          {c.username}
        </h3>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-[var(--cream-faint)] tabular-nums">
          <UserBadges user={c} size="sm" hideCreator />
          <span>{t("creators_page.followers_other", { count: followerCount.toLocaleString(i18n.language === "no" ? "nb-NO" : i18n.language) })}</span>
        </div>
        {(c.creator_bio || c.bio) && (
          <p className="text-sm text-[var(--cream-soft)] mt-1 line-clamp-2">
            {c.creator_bio || c.bio}
          </p>
        )}
        {loc && (
          <div className="font-meta mt-2 text-[var(--cream-faint)] inline-flex items-center gap-1.5">
            {c.country && <CountryFlag iso={c.country} size="sm" data-testid={`creator-flag-${c.username}`} />}
            <span className="truncate">{loc}</span>
          </div>
        )}
      </div>
    </Link>
  );
}

export default function Skapere() {
  const { t } = useTranslation();
  const SORTS = [
    { value: "popular", label: t("creators_page.sort_most_followed") },
    { value: "newest",  label: t("creators_page.sort_newest") },
    { value: "active",  label: t("creators_page.sort_most_active") },
  ];
  const [featured, setFeatured] = useState([]);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filter-state synkes til URL-query så filteret overlever profil-besøk + tilbake-navigasjon.
  const [sp, setSp] = useSearchParams();
  const initGender = sp.get("gender") || "";
  const initCountry = sp.get("country") || null;
  const initFylke = sp.get("fylke") || "";
  const initSort = sp.get("sort") || "popular";
  const [gender, setGender] = useState(initGender);
  const [country, setCountry] = useState(initCountry);
  const [fylke, setFylke] = useState(initFylke);
  const [sort, setSort] = useState(initSort);
  // "applied" = filtre som faktisk er sendt til backend. Dropdown-endringer oppdaterer draft; Søk-knappen commit'er til applied.
  const [applied, setApplied] = useState({ gender: initGender, country: initCountry, fylke: initFylke, sort: initSort });
  const [locations, setLocations] = useState({ countries: [], top_countries: ["NO","SE","DK","GB","US"], subdivisions: {}, fylker: [] });

  useEffect(() => {
    api.get("/search/locations").then((r) => {
      setLocations(r.data);
    }).catch(() => {});
  }, []);

  // Reset fylke når land endres (men ikke ved første mount så URL-basert fylke overlever)
  const didMountRef = React.useRef(false);
  useEffect(() => {
    if (didMountRef.current) setFylke("");
    else didMountRef.current = true;
  }, [country]);

  const activeSubdivisions = React.useMemo(() => {
    if (country && locations.subdivisions?.[country]) return locations.subdivisions[country];
    if (!country || country === "NO") return locations.fylker || [];
    return [];
  }, [country, locations]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api.get("/creators/featured?limit=4"),
      api.get("/creators", { params: { gender: applied.gender || undefined, country: applied.country || undefined, fylke: applied.fylke || undefined, sort: applied.sort, limit: 60 } }),
    ]).then(([f, l]) => {
      if (!mounted) return;
      setFeatured(f.data?.results || []);
      setList(l.data?.results || []);
      setTotal(l.data?.total || 0);
    }).catch(() => {
      setFeatured([]); setList([]); setTotal(0);
    }).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [applied]);

  const applyFilters = () => {
    setApplied({ gender, country, fylke, sort });
    // Synk til URL slik at profil-besøk + tilbake gjenoppretter samme filter
    const next = {};
    if (gender) next.gender = gender;
    if (country) next.country = country;
    if (fylke) next.fylke = fylke;
    if (sort && sort !== "popular") next.sort = sort;
    setSp(next, { replace: true });
  };

  const isDirty = gender !== applied.gender || country !== applied.country || fylke !== applied.fylke || sort !== applied.sort;

  const featuredIds = new Set(featured.map((c) => c.id));
  const restList = list.filter((c) => !featuredIds.has(c.id));

  return (
    <Layout noVikingPattern>
      <div style={{ background: "#141116", minHeight: "calc(100vh - 60px)" }} className="-mx-4 lg:-mx-8 -mt-6 px-4 lg:px-8 py-8">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <h1 className="font-fraunces italic text-5xl text-[var(--cream)]">{t("creators_page.title")}.</h1>
          <p className="text-[var(--cream-soft)] text-sm mt-2 max-w-xl">
            {t("creators_page.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <select
            data-testid="skapere-filter-gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className="bg-[var(--noir-elev)] border border-white/10 px-3 py-2 text-sm text-[var(--cream)] min-w-[140px]"
          >
            {GENDERS.map((g) => (
              <option key={g || "all"} value={g}>
                {g
                  ? (t(`search_page.${ {kvinne:'women',mann:'men','ikke-binær':'other',par:'couples'}[g] || 'other' }`))
                  : t("creators_page.filter_all_genders")}
              </option>
            ))}
          </select>
          <div className="min-w-[180px]">
            <CountrySelect
              value={country}
              onChange={setCountry}
              allCountries={locations.countries || []}
              topCountries={locations.top_countries || ["NO","SE","DK","GB","US"]}
              compact
              testIdPrefix="skapere-filter-country"
            />
          </div>
          <select
            data-testid="skapere-filter-fylke"
            value={fylke}
            onChange={(e) => setFylke(e.target.value)}
            disabled={activeSubdivisions.length === 0}
            className="bg-[var(--noir-elev)] border border-white/10 px-3 py-2 text-sm text-[var(--cream)] min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">
              {country === "SE" ? t("country_select.fylke_se")
                : country === "DK" ? t("country_select.fylke_dk")
                : country === "NO" || !country ? t("creators_page.filter_all_norway")
                : t("country_select.fylke_other")}
            </option>
            {activeSubdivisions.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select
            data-testid="skapere-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[var(--noir-elev)] border border-white/10 px-3 py-2 text-sm text-[var(--cream)]"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button
            type="button"
            onClick={applyFilters}
            disabled={loading || !isDirty}
            data-testid="skapere-search-btn"
            className="btn-magenta px-5 py-2 text-sm font-meta tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t("common.loading") : t("common.search", { defaultValue: "Søk" })}
          </button>
        </div>
      </div>

      {/* Featured-stripe */}
      {featured.length > 0 && (
        <section className="mb-12" data-testid="skapere-featured">
          <div className="font-meta mb-3 text-[var(--gold)]">{t("creators_page.featured_creators").toUpperCase()}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((c) => <CreatorCard key={c.id} c={c} featured />)}
          </div>
        </section>
      )}

      {/* Full liste */}
      <section data-testid="skapere-grid">
        <div className="flex items-baseline justify-between mb-3">
          <div className="font-meta text-[var(--cream-soft)]">
            {t("creators_page.all_creators").toUpperCase()}
          </div>
          {!loading && (
            <span className="text-xs text-[var(--cream-faint)] tabular-nums">
              {t(total === 1 ? "creators_page.creators_count_one" : "creators_page.creators_count_other", { count: total.toLocaleString("nb-NO") })}
            </span>
          )}
        </div>

        {loading ? (
          <div className="panel p-12 text-center text-[var(--cream-soft)]">{t("common.loading")}</div>
        ) : restList.length === 0 && featured.length === 0 ? (
          <div className="panel-tight p-12 text-center">
            <p className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">{t("creators_page.empty_title")}</p>
            <p className="text-[var(--cream-soft)] text-sm max-w-md mx-auto">
              {t("creators_page.empty_desc")}
            </p>
          </div>
        ) : restList.length === 0 ? (
          <div className="panel-tight p-8 text-center text-[var(--cream-soft)] text-sm">
            {t("creators_page.no_match")}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {restList.map((c) => <CreatorCard key={c.id} c={c} />)}
          </div>
        )}
      </section>

      {/* OnlyFans-coaching reklame – nederst på Skapere-utforsk-siden */}
      <section data-testid="skapere-of-ad" className="mt-12 max-w-4xl mx-auto">
        <OnlyFansAdStripe variant="stripe" />
      </section>
      </div>
    </Layout>
  );
}
